import { ChangeEvent, useEffect, useRef, useState } from 'react';
import {
    ISameCaseMatchedCasesSearchModel,
    SameCaseSortColumns,
} from '../../../../../pages/quad/same-cases-admin/manage/useGetSameCasesQuery';

import { CheckmateSameCaseGroupIcon } from '../../../../../shared/CheckmateIcons';
import { IMatchedCasesSearchResponseModel } from '../../../../../pages/quad/same-cases/view/IMatchedCasesSearchResponseModel';
import { IconButton } from '../../../../../shared/buttons/IconButton';
import { Link } from 'react-router-dom';
import { LocalRoutes } from '../../../../../utilities/LocalRoutes';
import { NoResultsDisplay } from '../../NoResultsDisplay';
import { SameCaseModal } from '../../../../shared/modals/SameCaseModal';
import { SortableHeader } from '../../../../shared/SortableHeader';
import { Table } from 'react-bootstrap';
import classNames from 'classnames';
import styles from './SameCaseAdminSearchResults.module.scss';
import { useAuthContext } from '../../../../../contexts/AuthContext';
import { useCaseFileModelContext } from '../../../../../contexts/useCaseFileModelContext';

export interface ISameCaseAdminSearchResultsProps {
    currentFilters: ISameCaseMatchedCasesSearchModel;
    isFilterPaneOpen: boolean;
    isLoading: boolean;
    onSort: (key: string, order: string, subKey?: string) => void;
    onSelectedCaseGuidsChange: (selectedCaseGuids: string[]) => void;
    onToggleFilterPane: () => void;
    selectedCaseGuids: string[];
    results: IMatchedCasesSearchResponseModel[];
}

export function SameCaseAdminSearchResults(props: ISameCaseAdminSearchResultsProps) {
    const { user } = useAuthContext();
    const { caseFileModelId } = useCaseFileModelContext();

    const selectAllCheckboxRef = useRef<HTMLInputElement>(null);
    const [sameCaseGuid, setSameCaseGuid] = useState<string | undefined>(undefined);
    const hasRows = (props.results?.length ?? 0) > 0;

    const handleRowSelected = (e: ChangeEvent<HTMLInputElement>) => {
        if (e.target.checked) {
            props.onSelectedCaseGuidsChange([...props.selectedCaseGuids, e.target.value]);
        } else {
            props.onSelectedCaseGuidsChange(
                props.selectedCaseGuids.filter((guid) => guid != e.target.value)
            );
        }
    };

    const handleSameCaseModalClose = () => {
        setSameCaseGuid(undefined);
    };

    const showSameCaseModal = (sameCaseGuid: string) => {
        setSameCaseGuid(sameCaseGuid);
    };

    useEffect(() => {
        const isIndeterminate =
            props.selectedCaseGuids.length > 0 &&
            props.selectedCaseGuids.length < props.results.length;

        if (selectAllCheckboxRef.current) {
            selectAllCheckboxRef.current.indeterminate = isIndeterminate;
        }

        props.onSelectedCaseGuidsChange(props.selectedCaseGuids);
    }, [selectAllCheckboxRef, props.selectedCaseGuids]);

    const selectedCases = props.results.filter((item) =>
        props.selectedCaseGuids.includes(item.caseGuid)
    );

    return (
        <>
            <Table size="sm">
                <thead>
                    <tr>
                        <th></th>
                        <SortableHeader
                            active={
                                props.currentFilters.sortModel.sortColumn ===
                                SameCaseSortColumns.OrganizationName
                            }
                            currentSortModel={props.currentFilters.sortModel}
                            headerText="Zone"
                            sortKey={SameCaseSortColumns.OrganizationName.toString()}
                            onSort={props.onSort}
                            noWrap
                        />
                        <SortableHeader
                            active={
                                props.currentFilters.sortModel.sortColumn ===
                                SameCaseSortColumns.CaseName
                            }
                            currentSortModel={props.currentFilters.sortModel}
                            headerText="Case Name"
                            sortKey={SameCaseSortColumns.CaseName.toString()}
                            onSort={props.onSort}
                            noWrap
                        />
                        <SortableHeader
                            active={
                                props.currentFilters.sortModel.sortColumn ===
                                SameCaseSortColumns.CaseNumber
                            }
                            currentSortModel={props.currentFilters.sortModel}
                            headerText="Case Number"
                            sortKey={SameCaseSortColumns.CaseNumber.toString()}
                            onSort={props.onSort}
                            noWrap
                        />
                        <SortableHeader
                            active={
                                props.currentFilters.sortModel.sortColumn ===
                                SameCaseSortColumns.Jurisdiction
                            }
                            currentSortModel={props.currentFilters.sortModel}
                            headerText="Jurisdiction"
                            sortKey={SameCaseSortColumns.Jurisdiction.toString()}
                            onSort={props.onSort}
                            noWrap
                        />
                        <th>Case Status</th>
                        <th>Plaintiff Firm</th>
                        <th>National Plaintiff Firm</th>
                        <SortableHeader
                            active={
                                props.currentFilters.sortModel.sortColumn ===
                                SameCaseSortColumns.SameCase
                            }
                            currentSortModel={props.currentFilters.sortModel}
                            headerText="Same Case Group"
                            sortKey={SameCaseSortColumns.SameCase.toString()}
                            onSort={props.onSort}
                            noWrap
                        />
                    </tr>
                </thead>
                <tbody>
                    {props.results.map((row: IMatchedCasesSearchResponseModel) => {
                        const userHasAccessToZone = user.organizationRoles?.some(
                            (orgRole) => orgRole.guid === row.organizationGuid
                        );
                        const caseUrl = `/zone/${row.organizationGuid}${LocalRoutes.CaseFile.replace(
                            ':guid',
                            row.caseGuid
                        )}`;

                        const thereAreSelectedCases = selectedCases.length > 0;
                        const isApprovedSameCase = row.sameCaseGuid !== null;

                        const firstSelectedCase = selectedCases?.at(0);

                        const caseNumberMatch =
                            firstSelectedCase?.caseNumber
                                .replaceAll('-', '')
                                .replaceAll(' ', '') ===
                            row.caseNumber.replaceAll('-', '').replaceAll(' ', '');

                        const jurisdictionMatch =
                            firstSelectedCase?.jurisdictionGuid === row.jurisdictionGuid;

                        const isEnabled =
                            (!isApprovedSameCase && !thereAreSelectedCases) ||
                            (!isApprovedSameCase &&
                                thereAreSelectedCases &&
                                jurisdictionMatch &&
                                caseNumberMatch);

                        return (
                            <tr
                                key={row.caseGuid}
                                style={{ verticalAlign: 'middle' }}
                                className={classNames({ [styles.disabledRow]: !isEnabled })}
                            >
                                {hasRows && (
                                    <td>
                                        <input
                                            checked={props.selectedCaseGuids.includes(row.caseGuid)}
                                            className="form-check-input"
                                            disabled={!isEnabled}
                                            onChange={handleRowSelected}
                                            type="checkbox"
                                            value={row.caseGuid}
                                        />
                                    </td>
                                )}
                                <td>{row.detailedOrganizationName}</td>
                                <td>
                                    {userHasAccessToZone ? (
                                        <Link to={caseUrl} target="_blank">
                                            {row.caseName}
                                        </Link>
                                    ) : (
                                        <>{row.caseName}</>
                                    )}
                                </td>
                                <td>{row.caseNumber}</td>
                                <td>{row.jurisdiction}</td>
                                <td>{row.status}</td>
                                <td>{row.plaintiffFirm}</td>
                                <td>{row.nationalPlaintiffFirm}</td>
                                <td>
                                    {isApprovedSameCase && (
                                        <IconButton
                                            buttonCssClass="btn btn-no-bg"
                                            buttonTitle="View Same Case Group"
                                            icon={<CheckmateSameCaseGroupIcon className="text-blue" />}
                                            onClick={() => showSameCaseModal(row.sameCaseGuid!)}
                                        />
                                    )}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </Table>
            <NoResultsDisplay
                onShowFiltersClick={() => props.onToggleFilterPane()}
                show={!hasRows && !props.isLoading}
            />
            <SameCaseModal
                cancelText={'OK'}
                onCancel={handleSameCaseModalClose}
                onClose={handleSameCaseModalClose}
                open={!!sameCaseGuid}
                params={{
                    sameCaseGuid: sameCaseGuid || '',
                    caseFileModelId: caseFileModelId,
                }}
                title={'Same Case Group'}
            />
        </>
    );
}
